import {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {api, authEndpoints, clearAuthToken, getApiErrorMessage, setAuthToken} from '@/lib/api';

const AuthContext = createContext(null);
const STORAGE_KEY = 'fleetcontrol_auth';

const persistSession = (nextUser) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUser));
    } catch { /* ignore */
    }
};

const clearSession = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */
    }
    clearAuthToken();
};

const extractAuthToken = (data) => {
    const source = data?.data && typeof data.data === 'object' ? data.data : data;

    return (
        data?.token ||
        data?.access_token ||
        data?.bearer_token ||
        data?.plainTextToken ||
        data?.plain_text_token ||
        data?.authorization?.token ||
        data?.authorisation?.token ||
        source?.token ||
        source?.access_token ||
        source?.bearer_token ||
        source?.plainTextToken ||
        source?.plain_text_token ||
        source?.authorization?.token ||
        source?.authorisation?.token ||
        ''
    );
};

const normalizeRole = (rawUser) => {
    const roles = rawUser?.roles;
    const firstRole = Array.isArray(roles) ? roles[0] : null;

    if (rawUser?.role) return typeof rawUser.role === 'string' ? rawUser.role : rawUser.role.name;
    if (firstRole) return typeof firstRole === 'string' ? firstRole : firstRole.name;
    if (rawUser?.is_super_admin) return 'superadmin';

    return 'condutor';
};

const normalizeUser = (rawUser) => {
    const source = rawUser?.user || rawUser?.data?.user || rawUser?.data || rawUser;
    const hasEmailVerificationState = Object.prototype.hasOwnProperty.call(source || {}, 'email_verified_at') ||
        Object.prototype.hasOwnProperty.call(source || {}, 'emailVerified') ||
        Object.prototype.hasOwnProperty.call(source || {}, 'email_verified') ||
        Object.prototype.hasOwnProperty.call(source || {}, 'has_verified_email');

    return {
        id: source?.id,
        email: source?.email || '',
        name: source?.name || source?.full_name || source?.display_name || source?.email || '',
        role: normalizeRole(source),
        emailVerified: hasEmailVerificationState
            ? Boolean(source?.email_verified_at || source?.emailVerified || source?.email_verified || source?.has_verified_email)
            : true,
        mustChangePassword: Boolean(
            source?.mustChangePassword ||
            source?.must_change_password ||
            source?.force_password_change ||
            source?.password_reset_required
        ),
        // Spatie direct permissions — string array of gest_* permission names
        permissions: Array.isArray(source?.permissions)
            ? source.permissions.map(p => (typeof p === 'string' ? p : p?.name)).filter(Boolean)
            : [],
        // Scope restrictions (populated alongside user by the API)
        permissionDepartments: Array.isArray(source?.permission_departments) ? source.permission_departments : [],
        permissionResources: Array.isArray(source?.permission_resources) ? source.permission_resources : [],
        raw: source,
    };
};

export const AuthProvider = ({children}) => {
    const [user, setUser] = useState(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const boot = async () => {
            const isPublicEmailVerification = (() => {
                try {
                    return window.location.pathname.startsWith('/email/verify');
                } catch {
                    return false;
                }
            })();

            if (isPublicEmailVerification) {
                clearSession();
                if (!cancelled) {
                    setUser(null);
                    setReady(true);
                }
                return;
            }

            const token = (() => {
                try {
                    return localStorage.getItem('fleetcontrol_auth_token') || '';
                } catch {
                    return '';
                }
            })();

            if (!token) {
                clearSession();
                if (!cancelled) {
                    setUser(null);
                    setReady(true);
                }
                return;
            }

            try {
                const raw = localStorage.getItem(STORAGE_KEY);
                if (raw && !cancelled) setUser(JSON.parse(raw));
            } catch { /* ignore */
            }

            try {
                const response = await api.get(authEndpoints.user);
                const nextUser = normalizeUser(response.data);
                if (!cancelled) {
                    setUser(nextUser);
                    persistSession(nextUser);
                }
            } catch {
                clearSession();
                if (!cancelled) setUser(null);
            } finally {
                if (!cancelled) setReady(true);
            }
        };

        boot();

        return () => {
            cancelled = true;
        };
    }, []);

    const login = useCallback(async ({email, password}) => {
        try {
            await api.get(authEndpoints.csrf);
            const response = await api.post(authEndpoints.login, {email, password});
            setAuthToken(extractAuthToken(response.data));

            const userResponse = response.data?.user ? response : await api.get(authEndpoints.user);
            const nextUser = normalizeUser(userResponse.data);

            setUser(nextUser);
            persistSession(nextUser);

            return {
                ok: true,
                name: nextUser.name,
                role: nextUser.role,
                email: nextUser.email,
                mustChangePassword: nextUser.mustChangePassword,
                permissions: nextUser.permissions,
            };
        } catch (error) {
            return {
                ok: false,
                error: getApiErrorMessage(error, 'Credenciais invalidas.'),
            };
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post(authEndpoints.logout);
        } catch { /* ignore logout API errors; local session still needs to end */
        }
        clearSession();
        setUser(null);
    }, []);

    const changeMyPassword = useCallback(async ({currentPassword, newPassword}) => {
        if (!user) return {ok: false, error: 'Sem sessão ativa.'};

        try {
            await api.post(authEndpoints.changePassword, {
                user_id: user.id,
                current_password: currentPassword,
                password: newPassword,
                password_confirmation: newPassword,
                currentPassword,
                newPassword,
                new_password: newPassword,
            });

            const updated = {...user, mustChangePassword: false};
            persistSession(updated);
            setUser(updated);
            return {ok: true, user: updated};
        } catch (error) {
            return {
                ok: false,
                error: getApiErrorMessage(error, 'Nao foi possível atualizar a palavra-passe.'),
            };
        }
    }, [user]);

    const refreshUser = useCallback(async () => {
        try {
            const response = await api.get(authEndpoints.user);
            const nextUser = normalizeUser(response.data);
            setUser(nextUser);
            persistSession(nextUser);
            return {ok: true, user: nextUser};
        } catch (error) {
            return {
                ok: false,
                error: getApiErrorMessage(error, 'Nao foi possível atualizar a sessão.'),
            };
        }
    }, []);

    const sendEmailVerification = useCallback(async ({email} = {}) => {
        try {
            const endpoint = user
                ? authEndpoints.emailVerificationNotification
                : authEndpoints.publicEmailVerificationNotification;
            await api.post(endpoint, email ? {email} : {});
            return {ok: true};
        } catch (error) {
            return {
                ok: false,
                error: getApiErrorMessage(error, 'Nao foi possível enviar o email de validação.'),
            };
        }
    }, [user]);

    const requestPasswordReset = useCallback(async ({email}) => {
        try {
            await api.get(authEndpoints.csrf);
            await api.post(authEndpoints.password, {email});
            return {ok: true};
        } catch (error) {
            return {
                ok: false,
                error: getApiErrorMessage(error, 'Nao foi possível enviar o email de reposição.'),
            };
        }
    }, []);

    const resetPassword = useCallback(async ({email, token, password}) => {
        try {
            await api.get(authEndpoints.csrf);
            await api.post(authEndpoints.resetPassword, {
                email,
                token,
                password,
                password_confirmation: password,
            });
            clearSession();
            setUser(null);
            return {ok: true};
        } catch (error) {
            return {
                ok: false,
                error: getApiErrorMessage(error, 'Nao foi possível repor a palavra-passe.'),
            };
        }
    }, []);

    const verifyEmail = useCallback(async ({id, hash, queryString = '', skipRefresh = false}) => {
        try {
            const response = await api.get(authEndpoints.verifyEmail(id, hash, queryString));
            if (skipRefresh) {
                return {
                    ok: true,
                    passwordResetLink: response.data?.password_reset_link || response.data?.passwordResetLink || '',
                };
            }
            if (!user) return {ok: true};
            const refreshed = await refreshUser();
            if (!refreshed.ok) return refreshed;
            return {
                ok: true,
                user: refreshed.user,
                passwordResetLink: response.data?.password_reset_link || response.data?.passwordResetLink || '',
            };
        } catch (error) {
            return {
                ok: false,
                error: getApiErrorMessage(error, 'Nao foi possível validar o email.'),
            };
        }
    }, [refreshUser, user]);

    const setVerifiedEmailPassword = useCallback(async ({id, hash, queryString = '', password, passwordResetLink = ''}) => {
        try {
            const endpoint = passwordResetLink
                ? new URL(passwordResetLink, api.defaults.baseURL).toString()
                : authEndpoints.verifiedEmailPassword(id, hash, queryString);

            await api.post(endpoint, {
                password,
                password_confirmation: password,
            });
            clearSession();
            setUser(null);
            return {ok: true};
        } catch (error) {
            return {
                ok: false,
                error: getApiErrorMessage(error, 'Nao foi possível definir a palavra-passe.'),
            };
        }
    }, []);

    const adminResetPassword = () => {
        return {
            ok: false,
            error: 'Esta ação tem de ser feita através da API de autenticação.',
        };
    };

    return (
        <AuthContext.Provider value={{
            user, ready, login, logout,
            changeMyPassword, adminResetPassword,
            refreshUser, sendEmailVerification, requestPasswordReset,
            resetPassword, verifyEmail, setVerifiedEmailPassword,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
};
